import { supabase } from '../lib/supabase';

// ============================================
// TRANSFER MARKETPLACE SERVICE
// ============================================

// Transfer Inquiries
export const transferInquiryService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('transfer_inquiries')
      ?.select('*, club:user_profiles!transfer_inquiries_club_id_fkey(full_name, organization)')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('transfer_inquiries')
      ?.select('*, club:user_profiles!transfer_inquiries_club_id_fkey(full_name, organization)')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async create(inquiryData) {
    const snakeCaseData = this.toSnakeCase(inquiryData);
    const { data, error } = await supabase
      ?.from('transfer_inquiries')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, inquiryData) {
    const snakeCaseData = this.toSnakeCase(inquiryData);
    const { data, error } = await supabase
      ?.from('transfer_inquiries')
      ?.update(snakeCaseData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async incrementViews(id) {
    const { error } = await supabase?.rpc('increment_inquiry_views', { inquiry_id: id });
    if (error) throw error;
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      clubId: obj?.club_id,
      club: obj?.club,
      title: obj?.title,
      description: obj?.description,
      positionRequired: obj?.position_required,
      budgetMin: obj?.budget_min,
      budgetMax: obj?.budget_max,
      currency: obj?.currency,
      timelineDeadline: obj?.timeline_deadline,
      playerSpecifications: obj?.player_specifications,
      status: obj?.status,
      viewsCount: obj?.views_count,
      responsesCount: obj?.responses_count,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      club_id: obj?.clubId,
      title: obj?.title,
      description: obj?.description,
      position_required: obj?.positionRequired,
      budget_min: obj?.budgetMin,
      budget_max: obj?.budgetMax,
      currency: obj?.currency,
      timeline_deadline: obj?.timelineDeadline,
      player_specifications: obj?.playerSpecifications,
      status: obj?.status
    };
  }
};

// Transfer Responses
export const transferResponseService = {
  async getByInquiryId(inquiryId) {
    const { data, error } = await supabase
      ?.from('transfer_responses')
      ?.select('*, respondent:user_profiles!transfer_responses_respondent_id_fkey(full_name, organization), player:player_master_profiles(full_name, primary_position, current_market_value, profile_photo)')
      ?.eq('inquiry_id', inquiryId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async create(responseData) {
    const snakeCaseData = this.toSnakeCase(responseData);
    const { data, error } = await supabase
      ?.from('transfer_responses')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, responseData) {
    const snakeCaseData = this.toSnakeCase(responseData);
    const { data, error } = await supabase
      ?.from('transfer_responses')
      ?.update(snakeCaseData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      inquiryId: obj?.inquiry_id,
      respondentId: obj?.respondent_id,
      respondent: obj?.respondent,
      playerId: obj?.player_id,
      player: obj?.player ? {
        fullName: obj?.player?.full_name,
        primaryPosition: obj?.player?.primary_position,
        currentMarketValue: obj?.player?.current_market_value,
        profilePhoto: obj?.player?.profile_photo
      } : null,
      proposedTerms: obj?.proposed_terms,
      askingPrice: obj?.asking_price,
      currency: obj?.currency,
      availabilityDate: obj?.availability_date,
      additionalNotes: obj?.additional_notes,
      status: obj?.status,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      inquiry_id: obj?.inquiryId,
      respondent_id: obj?.respondentId,
      player_id: obj?.playerId,
      proposed_terms: obj?.proposedTerms,
      asking_price: obj?.askingPrice,
      currency: obj?.currency,
      availability_date: obj?.availabilityDate,
      additional_notes: obj?.additionalNotes,
      status: obj?.status
    };
  }
};

// Transfer Deals
export const transferDealService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('transfer_deals')
      ?.select('*, club:user_profiles!transfer_deals_club_id_fkey(full_name, organization), player_representative:user_profiles!transfer_deals_player_representative_id_fkey(full_name, organization), player:player_master_profiles(full_name, primary_position, current_market_value, profile_photo)')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('transfer_deals')
      ?.select('*, club:user_profiles!transfer_deals_club_id_fkey(full_name, organization), player_representative:user_profiles!transfer_deals_player_representative_id_fkey(full_name, organization), player:player_master_profiles(full_name, primary_position, current_market_value, profile_photo)')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async create(dealData) {
    const snakeCaseData = this.toSnakeCase(dealData);
    const { data, error } = await supabase
      ?.from('transfer_deals')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, dealData) {
    const snakeCaseData = this.toSnakeCase(dealData);
    const { data, error } = await supabase
      ?.from('transfer_deals')
      ?.update(snakeCaseData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      inquiryId: obj?.inquiry_id,
      responseId: obj?.response_id,
      clubId: obj?.club_id,
      club: obj?.club,
      playerRepresentativeId: obj?.player_representative_id,
      playerRepresentative: obj?.player_representative,
      playerId: obj?.player_id,
      player: obj?.player ? {
        fullName: obj?.player?.full_name,
        primaryPosition: obj?.player?.primary_position,
        currentMarketValue: obj?.player?.current_market_value,
        profilePhoto: obj?.player?.profile_photo
      } : null,
      dealTitle: obj?.deal_title,
      currentStage: obj?.current_stage,
      proposedFee: obj?.proposed_fee,
      currency: obj?.currency,
      contractTerms: obj?.contract_terms,
      milestones: obj?.milestones,
      nextAction: obj?.next_action,
      nextDeadline: obj?.next_deadline,
      priorityLevel: obj?.priority_level,
      isUrgent: obj?.is_urgent,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at,
      completedAt: obj?.completed_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      inquiry_id: obj?.inquiryId,
      response_id: obj?.responseId,
      club_id: obj?.clubId,
      player_representative_id: obj?.playerRepresentativeId,
      player_id: obj?.playerId,
      deal_title: obj?.dealTitle,
      current_stage: obj?.currentStage,
      proposed_fee: obj?.proposedFee,
      currency: obj?.currency,
      contract_terms: obj?.contractTerms,
      milestones: obj?.milestones,
      next_action: obj?.nextAction,
      next_deadline: obj?.nextDeadline,
      priority_level: obj?.priorityLevel,
      is_urgent: obj?.isUrgent
    };
  }
};

// Deal Messages
export const dealMessageService = {
  async getByDealId(dealId) {
    const { data, error } = await supabase
      ?.from('deal_messages')
      ?.select('*, sender:user_profiles(full_name, avatar_url)')
      ?.eq('deal_id', dealId)
      ?.order('created_at', { ascending: true });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async create(messageData) {
    const snakeCaseData = this.toSnakeCase(messageData);
    const { data, error } = await supabase
      ?.from('deal_messages')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      dealId: obj?.deal_id,
      senderId: obj?.sender_id,
      sender: obj?.sender,
      messageType: obj?.message_type,
      content: obj?.content,
      attachments: obj?.attachments,
      isRead: obj?.is_read,
      createdAt: obj?.created_at
    };
  },

  toSnakeCase(obj) {
    if (!obj) return obj;
    return {
      deal_id: obj?.dealId,
      sender_id: obj?.senderId,
      message_type: obj?.messageType,
      content: obj?.content,
      attachments: obj?.attachments,
      is_read: obj?.isRead
    };
  }
};

// Deal Activity Log
export const dealActivityService = {
  async getByDealId(dealId) {
    const { data, error } = await supabase
      ?.from('deal_activity_log')
      ?.select('*, user:user_profiles(full_name)')
      ?.eq('deal_id', dealId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      dealId: obj?.deal_id,
      userId: obj?.user_id,
      user: obj?.user,
      activityType: obj?.activity_type,
      description: obj?.description,
      metadata: obj?.metadata,
      createdAt: obj?.created_at
    };
  }
};

// Real-time subscriptions
export const subscribeToInquiries = (callback) => {
  const channel = supabase
    ?.channel('transfer_inquiries_changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'transfer_inquiries'
      },
      (payload) => {
        console.log('Inquiry change detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

export const subscribeToDeals = (callback) => {
  const channel = supabase
    ?.channel('transfer_deals_changes')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'transfer_deals'
      },
      (payload) => {
        console.log('Deal change detected:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};